import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superadminApi } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { CURRENCIES, STRIPE_COUNTRIES, currencySymbol } from '@/lib/money'
import { TIME_ZONES, DEFAULT_TIME_ZONE_FOR_COUNTRY } from '@/lib/dates'
import { ArrowLeft, Check, Copy, Link2, Users, Building2, BookOpen, CalendarCheck, GraduationCap, Award, Inbox, Mail, KeyRound, UserPlus } from 'lucide-react'

function StatCard({ icon: Icon, label, value, sub }: { icon: typeof Users; label: string; value: number | string; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
            <p className="text-xs text-gray-500 truncate">{label}{sub && <span className="text-gray-400"> · {sub}</span>}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Staging and local run against Stripe test mode, whose dashboard lives under /test.
const STRIPE_DASHBOARD = /staging|localhost/.test(import.meta.env.VITE_API_URL ?? '')
  ? 'https://dashboard.stripe.com/test'
  : 'https://dashboard.stripe.com'

function StripeLink({ kind, id }: { kind: 'customers' | 'subscriptions'; id?: string | null }) {
  if (!id) return <span className="font-medium text-gray-900">None</span>
  return (
    <a
      href={`${STRIPE_DASHBOARD}/${kind}/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs text-indigo-600 hover:text-indigo-800 hover:underline break-all"
    >
      {id}
    </a>
  )
}

function fmt(d?: string | null) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}

function Field({ id, label, value, onChange, type = 'text', placeholder }: {
  id: string; label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

export default function TenantDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()

  const { data: t, isLoading } = useQuery({
    queryKey: ['superadmin-tenant', id],
    queryFn: () => superadminApi.getTenant(id).then(r => r.data),
    enabled: !!id,
  })

  // ── Details form ──────────────────────────────────────────────────────────
  const [form, setForm] = useState({ name: '', displayName: '', contactEmail: '', website: '', companyNumber: '', vatNumber: '' })
  const [detailsSaved, setDetailsSaved] = useState(false)
  useEffect(() => {
    if (t) setForm({
      name: t.name ?? '',
      displayName: t.displayName ?? '',
      contactEmail: t.contactEmail ?? '',
      website: t.website ?? '',
      companyNumber: t.companyNumber ?? '',
      vatNumber: t.vatNumber ?? '',
    })
  }, [t])

  const detailsMutation = useMutation({
    mutationFn: () => superadminApi.updateTenant(id, {
      name: form.name,
      displayName: form.displayName || null,
      contactEmail: form.contactEmail || null,
      website: form.website || null,
      companyNumber: form.companyNumber || null,
      vatNumber: form.vatNumber || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] })
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenants'] })
      setDetailsSaved(true); setTimeout(() => setDetailsSaved(false), 2500)
    },
  })

  // ── Billing form ──────────────────────────────────────────────────────────
  const [planTier, setPlanTier] = useState('Starter')
  const [isComped, setIsComped] = useState(false)
  const [billingSaved, setBillingSaved] = useState(false)
  useEffect(() => {
    if (t) {
      setPlanTier(t.planTier === 'Pro' ? 'Pro' : 'Starter')
      setIsComped(t.isComped)
    }
  }, [t])

  const billingMutation = useMutation({
    mutationFn: () => superadminApi.setTenantBilling(id, { planTier, isComped }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] })
      setBillingSaved(true); setTimeout(() => setBillingSaved(false), 2500)
    },
  })

  // ── Currency & region ────────────────────────────────────────────────────
  const [currency, setCurrency] = useState('GBP')
  const [country, setCountry] = useState('GB')
  const [timeZone, setTimeZone] = useState('Europe/London')
  const [currencySaved, setCurrencySaved] = useState(false)
  useEffect(() => {
    if (t) { setCurrency(t.currency); setCountry(t.country); setTimeZone(t.timeZoneId) }
  }, [t])

  const currencyMutation = useMutation({
    mutationFn: () => superadminApi.setTenantCurrency(id, { currency, country, timeZoneId: timeZone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] })
      setCurrencySaved(true); setTimeout(() => setCurrencySaved(false), 2500)
    },
  })

  // ── Free trainer seats (never on a bill, and separate from the paid add-on seats) ──
  const complimentaryMutation = useMutation({
    mutationFn: (seats: number) => superadminApi.setTenantComplimentarySeats(id, seats),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] }),
  })
  const [complimentarySeats, setComplimentarySeats] = useState('')

  // ── CertSlate Live room size (whether Live is on is the organisation's own choice, in My Company) ──
  const liveMutation = useMutation({
    mutationFn: (capacity: number) => superadminApi.setTenantLiveCapacity(id, capacity),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] }),
  })
  const [liveCapacity, setLiveCapacity] = useState('')

  // Changing the country suggests that country's usual time zone, unless a different one was chosen deliberately.
  const changeCountry = (next: string) => {
    if (timeZone === DEFAULT_TIME_ZONE_FOR_COUNTRY[country]) setTimeZone(DEFAULT_TIME_ZONE_FOR_COUNTRY[next] ?? timeZone)
    setCountry(next)
  }

  const saveCurrency = () => {
    if (t?.hasFinancialData && currency !== t.currency &&
        !window.confirm(
          `This tenant already has invoices or payments.

Switching from ${t.currency} to ${currency} keeps existing invoices and payments in ${t.currency}, ` +
          `but course and session prices are plain numbers and will now be read as ${currency}. Review them afterwards.

Continue?`)) return
    currencyMutation.mutate()
  }

  // ── Platform fee (Stripe Connect application fee) ───────────────────────────
  const [platformFeePounds, setPlatformFeePounds] = useState('1.00')
  const [platformFeeSaved, setPlatformFeeSaved] = useState(false)
  useEffect(() => {
    if (t) setPlatformFeePounds((t.platformFeeAmountPence / 100).toFixed(2))
  }, [t])

  const platformFeeMutation = useMutation({
    mutationFn: () => superadminApi.setTenantPlatformFee(id, {
      platformFeeAmountPence: Math.round(parseFloat(platformFeePounds || '0') * 100),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenant', id] })
      setPlatformFeeSaved(true); setTimeout(() => setPlatformFeeSaved(false), 2500)
    },
  })

  // ── Checkout link (bespoke/consulted onboarding) ────────────────────────────
  const [checkoutEmail, setCheckoutEmail] = useState('')
  const [checkoutPlan, setCheckoutPlan] = useState('Starter')
  const [checkoutTrialDays, setCheckoutTrialDays] = useState('0')
  const [checkoutSeats, setCheckoutSeats] = useState('0')
  const [checkoutCustomPriceId, setCheckoutCustomPriceId] = useState('')
  const [checkoutCouponId, setCheckoutCouponId] = useState('')
  const [showBespoke, setShowBespoke] = useState(false)
  const [checkoutLink, setCheckoutLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  useEffect(() => {
    if (t?.contactEmail) setCheckoutEmail(t.contactEmail)
  }, [t])

  const checkoutLinkMutation = useMutation({
    mutationFn: () => superadminApi.createTenantCheckoutLink(id, {
      email: checkoutEmail,
      planTier: checkoutPlan,
      trialDays: Number(checkoutTrialDays) || 0,
      extraTrainerSeats: Number(checkoutSeats) || 0,
      customPriceId: checkoutCustomPriceId || null,
      couponId: checkoutCouponId || null,
    }),
    onSuccess: (res) => setCheckoutLink(res.data.url),
  })

  function copyLink() {
    if (!checkoutLink) return
    navigator.clipboard.writeText(checkoutLink)
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000)
  }

  // ── Usage ────────────────────────────────────────────────────────────────
  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['superadmin-tenant-usage', id],
    queryFn: () => superadminApi.getTenantUsage(id).then(r => r.data),
    enabled: !!id,
  })

  // ── Accounts ─────────────────────────────────────────────────────────────
  const { data: accounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ['superadmin-tenant-accounts', id],
    queryFn: () => superadminApi.getTenantAccounts(id).then(r => r.data),
    enabled: !!id,
  })

  const [newAccountEmail, setNewAccountEmail] = useState('')
  const [newAccountFirstName, setNewAccountFirstName] = useState('')
  const [newAccountLastName, setNewAccountLastName] = useState('')
  const [accountActionMessage, setAccountActionMessage] = useState<string | null>(null)

  const addAccountMutation = useMutation({
    mutationFn: () => superadminApi.addTenantAccount(id, newAccountEmail, newAccountFirstName, newAccountLastName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenant-accounts', id] })
      setAccountActionMessage(`Invite sent to ${newAccountEmail}.`)
      setNewAccountEmail(''); setNewAccountFirstName(''); setNewAccountLastName('')
      setTimeout(() => setAccountActionMessage(null), 4000)
    },
  })

  const welcomeEmailMutation = useMutation({
    mutationFn: (email: string) => superadminApi.sendTenantWelcomeEmail(id, email),
    onSuccess: (_res, email) => {
      setAccountActionMessage(`Welcome email sent to ${email}.`)
      setTimeout(() => setAccountActionMessage(null), 4000)
    },
  })

  const passwordResetMutation = useMutation({
    mutationFn: (email: string) => superadminApi.sendTenantPasswordReset(id, email),
    onSuccess: (_res, email) => {
      setAccountActionMessage(`Password reset email sent to ${email}.`)
      setTimeout(() => setAccountActionMessage(null), 4000)
    },
  })

  if (isLoading || !t) return <p className="text-gray-500">Loading…</p>

  return (
    <div>
      <Link to="/superadmin/tenants" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={15} /> Back to tenants
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-2xl font-bold text-gray-900">{t.name}</h1>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${t.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
          {t.isActive ? 'Active' : 'Inactive'}
        </span>
        {t.isComped && <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">Comped</span>}
      </div>
      <p className="text-sm text-gray-500 mb-6 font-mono">{t.slug} · {t.userCount} user{t.userCount !== 1 ? 's' : ''} · created {fmt(t.createdAt)}</p>

      <Tabs defaultValue="details">
        <TabsList className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <form onSubmit={e => { e.preventDefault(); detailsMutation.mutate() }} className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader><CardTitle className="text-base">Tenant</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field id="name" label="Tenant name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Acme Training Ltd" />
                <Field id="displayName" label="Display name (branding)" value={form.displayName} onChange={v => setForm(f => ({ ...f, displayName: v }))} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field id="contactEmail" label="Contact email" type="email" value={form.contactEmail} onChange={v => setForm(f => ({ ...f, contactEmail: v }))} placeholder="info@acme.com" />
                <Field id="website" label="Website" value={form.website} onChange={v => setForm(f => ({ ...f, website: v }))} placeholder="https://acme.com" />
              </CardContent>
            </Card>
            {t.signup && (
              <Card>
                <CardHeader><CardTitle className="text-base">Signup</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-1">
                  <p>Signed up as: <span className="font-medium text-gray-900">{t.signup.signupEmail}</span></p>
                  {t.signup.phone && <p>Phone: <span className="font-medium text-gray-900">{t.signup.phone}</span></p>}
                  <p>Heard about us: <span className="font-medium text-gray-900">{t.signup.heardAboutLabel ?? 'Not answered'}</span>{t.signup.heardAboutOther && <span> — “{t.signup.heardAboutOther}”</span>}</p>
                  {(t.signup.utmSource || t.signup.utmMedium || t.signup.utmCampaign) && (
                    <p>Campaign: <span className="font-medium text-gray-900">{[t.signup.utmSource, t.signup.utmMedium, t.signup.utmCampaign].filter(Boolean).join(' / ')}</span></p>
                  )}
                  {t.signup.referrer && <p>Referrer: <span className="font-medium text-gray-900 break-all">{t.signup.referrer}</span></p>}
                  <p>Terms accepted: <span className="font-medium text-gray-900">{t.signup.termsAcceptedAt
                    ? `${fmt(t.signup.termsAcceptedAt)} · Terms ${t.signup.termsVersion ? `v${t.signup.termsVersion}` : 'version not recorded'}, Privacy Policy ${t.signup.privacyPolicyVersion ? `v${t.signup.privacyPolicyVersion}` : 'version not recorded'}`
                    : 'Not recorded (signed up before acceptance was tracked)'}</span></p>
                  <p>Product update emails: <span className="font-medium text-gray-900">{t.signup.productUpdateEmails}</span></p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-base">Company identity</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="companyNumber" label="Company number" value={form.companyNumber} onChange={v => setForm(f => ({ ...f, companyNumber: v }))} />
                <Field id="vatNumber" label="VAT number" value={form.vatNumber} onChange={v => setForm(f => ({ ...f, vatNumber: v }))} />
              </CardContent>
            </Card>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={detailsMutation.isPending}>{detailsMutation.isPending ? 'Saving…' : 'Save details'}</Button>
              {detailsSaved && <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Check size={14} /> Saved</span>}
            </div>
          </form>
        </TabsContent>

        <TabsContent value="billing">
          <div className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader><CardTitle className="text-base">Current subscription</CardTitle></CardHeader>
              <CardContent className="text-sm text-gray-600 space-y-1">
                <p>Plan tier: <span className="font-medium text-gray-900">{t.planTier}</span></p>
                <p>Status: <span className="font-medium text-gray-900">{t.subscriptionStatus}</span>{t.cancelAtPeriodEnd && <span className="text-amber-600"> · cancels {fmt(t.currentPeriodEndsAt)}</span>}</p>
                <p>Billing: <span className="font-medium text-gray-900">{t.isComped ? 'Comped (manual)' : t.hasStripeSubscription ? 'Stripe' : 'None'}</span></p>
                <p>Billing interval: <span className="font-medium text-gray-900">{t.billingInterval === 'Annual' ? 'Yearly (paid up front)' : 'Monthly'}</span></p>
                <p>Trainer add-on seats: <span className="font-medium text-gray-900">{t.additionalTrainerSeats}</span></p>
                <p>Stripe customer: <StripeLink kind="customers" id={t.stripeCustomerId} /></p>
                <p>Stripe subscription: <StripeLink kind="subscriptions" id={t.stripeSubscriptionId} /></p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Set plan &amp; comp</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="planTier">Feature tier</Label>
                  <select id="planTier" value={planTier} onChange={e => setPlanTier(e.target.value)}
                    className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                    <option value="Starter">Starter</option>
                    <option value="Pro">Pro</option>
                  </select>
                  <p className="text-xs text-gray-400">Controls which features the tenant can access.</p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={isComped} onChange={e => setIsComped(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Comped (no Stripe subscription)</p>
                    <p className="text-xs text-gray-500">Tenant is entitled to the tier above for free and is never treated as lapsed. For pilots/partners.</p>
                  </div>
                </label>
                {t.hasStripeSubscription && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    This tenant has a Stripe subscription — its tier is normally driven by Stripe and may be overwritten on the next billing event. Use this only to override intentionally.
                  </p>
                )}
                <div className="flex items-center gap-3 pt-1">
                  <Button onClick={() => billingMutation.mutate()} disabled={billingMutation.isPending}>{billingMutation.isPending ? 'Saving…' : 'Save billing'}</Button>
                  {billingSaved && <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Check size={14} /> Saved</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Currency, region &amp; time zone</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                  <div className="space-y-1.5">
                    <Label htmlFor="tenantCurrency">Currency</Label>
                    <select id="tenantCurrency" value={currency} onChange={e => setCurrency(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                      {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tenantCountry">Country / region</Label>
                    <select id="tenantCountry" value={country} onChange={e => changeCountry(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                      {STRIPE_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="tenantTimeZone">Time zone</Label>
                    <select id="tenantTimeZone" value={timeZone} onChange={e => setTimeZone(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                      {TIME_ZONES.map(z => <option key={z.id} value={z.id}>{z.label}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  Course and session prices, bookings, invoices and payments for this tenant are shown and charged in this currency.
                  The country / region also sets date and time style (US: month-first, 12-hour), address wording and the calendar's first day of the week.
                  The time zone decides which day session dates and reminders fall on. The country is also used when the tenant connects Stripe, so set it before they onboard (it can't be changed on an existing Stripe account).
                  CertSlate's own subscription billing stays in GBP.
                </p>
                <div className="flex items-center gap-3 pt-1">
                  <Button onClick={saveCurrency} disabled={currencyMutation.isPending}>{currencyMutation.isPending ? 'Saving…' : 'Save currency'}</Button>
                  {currencySaved && <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Check size={14} /> Saved</span>}
                  {currencyMutation.isError && <span className="text-sm text-red-600">Couldn't save — check the values and try again.</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Complimentary trainer seats</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-gray-500">
                  Free trainer seats for a pilot, a partner or goodwill. They are added to the plan&apos;s included seats and any paid add-on seats
                  ({t.additionalTrainerSeats} paid at the moment), never appear on a bill, and don&apos;t touch Stripe.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="complimentary-seats" className="text-sm text-gray-700">Complimentary seats</label>
                  <input
                    id="complimentary-seats"
                    type="number" min={0} max={200}
                    value={complimentarySeats === '' ? t.complimentaryTrainerSeats : complimentarySeats}
                    onChange={e => setComplimentarySeats(e.target.value)}
                    className="h-9 w-24 rounded-md border border-gray-300 px-2 text-sm"
                  />
                  <Button
                    size="sm" variant="outline"
                    disabled={complimentaryMutation.isPending || complimentarySeats === '' || Number(complimentarySeats) === t.complimentaryTrainerSeats}
                    onClick={() => complimentaryMutation.mutate(Number(complimentarySeats), { onSuccess: () => setComplimentarySeats('') })}
                  >Save</Button>
                  <span className="text-xs text-gray-400">0–200. Set to 0 to take them away.</span>
                </div>
                {complimentaryMutation.isError && <p className="text-sm text-red-600">Couldn&apos;t save. Please check the value and try again.</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Live sessions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-gray-500">
                  CertSlate Live is on for every organisation by default; they can switch it off themselves in My Company &gt; Live.
                  The room size is yours to set because bigger rooms cost more to run.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="live-capacity" className="text-sm text-gray-700">Room capacity</label>
                  <input
                    id="live-capacity"
                    type="number" min={10} max={300}
                    value={liveCapacity === '' ? t.liveCapacity : liveCapacity}
                    onChange={e => setLiveCapacity(e.target.value)}
                    className="h-9 w-24 rounded-md border border-gray-300 px-2 text-sm"
                  />
                  <Button
                    size="sm" variant="outline"
                    disabled={liveMutation.isPending || liveCapacity === '' || Number(liveCapacity) === t.liveCapacity}
                    onClick={() => liveMutation.mutate(Number(liveCapacity), { onSuccess: () => setLiveCapacity('') })}
                  >Save</Button>
                  <span className="text-xs text-gray-400">10–300 people per room (default 100).</span>
                </div>
                {liveMutation.isError && <p className="text-sm text-red-600">Couldn&apos;t save. Please check the value and try again.</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Stripe Connect administration fee</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="platformFee">Administration fee per transaction ({currencySymbol(t.currency)})</Label>
                  <Input
                    id="platformFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={platformFeePounds}
                    onChange={e => setPlatformFeePounds(e.target.value)}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-gray-400">
                    Taken via Stripe Connect on each invoice/session payment this tenant processes. Set to 0 for no fee.
                    The tenant chooses (in their own Settings) whether this is deducted from their proceeds or passed on to their customer.
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <Button onClick={() => platformFeeMutation.mutate()} disabled={platformFeeMutation.isPending}>{platformFeeMutation.isPending ? 'Saving…' : 'Save administration fee'}</Button>
                  {platformFeeSaved && <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Check size={14} /> Saved</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 size={16} /> Connect billing (checkout link)</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-gray-500">
                  Generate a Stripe checkout link for a bespoke/consulted deal. The customer pays via this link and
                  their subscription is linked back to this tenant automatically — no new tenant is created.
                </p>
                <Field id="checkoutEmail" label="Customer email" type="email" value={checkoutEmail} onChange={setCheckoutEmail} placeholder="billing@customer.com" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="checkoutPlan">Plan tier</Label>
                    <select id="checkoutPlan" value={checkoutPlan} onChange={e => setCheckoutPlan(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                      <option value="Starter">Starter</option>
                      <option value="Pro">Pro</option>
                    </select>
                  </div>
                  <Field id="checkoutTrialDays" label="Trial days" type="number" value={checkoutTrialDays} onChange={setCheckoutTrialDays} placeholder="0" />
                  <Field id="checkoutSeats" label="Extra trainer seats" type="number" value={checkoutSeats} onChange={setCheckoutSeats} placeholder="0" />
                </div>
                <p className="text-xs text-gray-400">Admin-onboarded customers default to no trial. Set trial days above only if this deal includes one.</p>

                <button type="button" onClick={() => setShowBespoke(s => !s)} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                  {showBespoke ? '− Hide' : '+ Add'} bespoke pricing (custom price / coupon)
                </button>
                {showBespoke && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-md border border-gray-200 p-3">
                    <Field id="checkoutCustomPriceId" label="Custom Stripe price ID" value={checkoutCustomPriceId} onChange={setCheckoutCustomPriceId} placeholder="price_..." />
                    <Field id="checkoutCouponId" label="Coupon ID" value={checkoutCouponId} onChange={setCheckoutCouponId} placeholder="e.g. BESPOKE20" />
                    <p className="text-xs text-gray-400 sm:col-span-2">
                      Custom price overrides the standard {checkoutPlan} price for the main line item (feature tier is still {checkoutPlan}).
                      A coupon must already exist in Stripe; setting one disables manual promo code entry at checkout.
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-3 pt-1">
                  <Button onClick={() => checkoutLinkMutation.mutate()} disabled={checkoutLinkMutation.isPending || !checkoutEmail}>
                    {checkoutLinkMutation.isPending ? 'Generating…' : 'Generate checkout link'}
                  </Button>
                </div>
                {checkoutLinkMutation.isError && (
                  <p className="text-xs text-red-600">Failed to generate link. Check the plan tier and email, then try again.</p>
                )}
                {checkoutLink && (
                  <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <code className="flex-1 text-xs text-gray-700 truncate">{checkoutLink}</code>
                    <button onClick={copyLink} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 shrink-0">
                      {linkCopied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="usage">
          {usageLoading || !usage ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-6 max-w-3xl">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <StatCard icon={Building2} label="Clients" value={usage.clientCount} />
                <StatCard icon={Users} label="Staff members" value={usage.staffMemberCount} sub={`${usage.activeStaffMemberCount} active`} />
                <StatCard icon={BookOpen} label="Courses" value={usage.courseCount} />
                <StatCard icon={CalendarCheck} label="Bookings" value={usage.bookingCount} sub={`${usage.upcomingBookingCount} upcoming`} />
                <StatCard icon={GraduationCap} label="Training records" value={usage.trainingRecordCount} />
                <StatCard icon={Award} label="Certificates issued" value={usage.certificateCount} />
              </div>

              {usage.pendingBookingEnquiryCount > 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600 shrink-0">
                        <Inbox size={18} />
                      </div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">{usage.pendingBookingEnquiryCount}</span> pending booking enqu{usage.pendingBookingEnquiryCount === 1 ? 'iry' : 'iries'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-1">
                  <p>Last booking created: <span className="font-medium text-gray-900">{fmt(usage.lastBookingCreatedAt)}</span></p>
                  <p>Last training record added: <span className="font-medium text-gray-900">{fmt(usage.lastTrainingRecordCreatedAt)}</span></p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="accounts">
          <div className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><UserPlus size={16} /> Add admin account</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-gray-500">
                  Creates an additional TenantAdmin login for this tenant — created with no password;
                  they'll get an invite email to set their own.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field id="newAccountFirstName" label="First name" value={newAccountFirstName} onChange={setNewAccountFirstName} placeholder="Jane" />
                  <Field id="newAccountLastName" label="Last name" value={newAccountLastName} onChange={setNewAccountLastName} placeholder="Smith" />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Field id="newAccountEmail" label="Email" type="email" value={newAccountEmail} onChange={setNewAccountEmail} placeholder="admin@customer.com" />
                  </div>
                  <Button
                    onClick={() => addAccountMutation.mutate()}
                    disabled={addAccountMutation.isPending || !newAccountEmail || !newAccountFirstName || !newAccountLastName}
                  >
                    {addAccountMutation.isPending ? 'Adding…' : 'Add account'}
                  </Button>
                </div>
                {addAccountMutation.isError && (
                  <p className="text-xs text-red-600">
                    {(addAccountMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                      ?? 'Failed to add account.'}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Accounts</CardTitle></CardHeader>
              <CardContent className="p-0">
                {accountActionMessage && (
                  <div className="px-6 pt-4">
                    <p className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Check size={14} /> {accountActionMessage}</p>
                  </div>
                )}
                {accountsLoading ? (
                  <p className="text-sm text-gray-500 px-6 py-6">Loading…</p>
                ) : accounts.length === 0 ? (
                  <p className="text-sm text-gray-400 px-6 py-6 text-center">No admin accounts yet.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {accounts.map(a => (
                      <div key={a.id} className="px-6 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {a.firstName || a.lastName ? `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() : a.email}
                          </p>
                          {(a.firstName || a.lastName) && <p className="text-xs text-gray-500 truncate">{a.email}</p>}
                          <p className="text-xs text-gray-400">
                            {a.isActive
                              ? <span className="text-green-600">Active</span>
                              : <span className="text-amber-600">Invite pending</span>}
                            {' · '}created {fmt(a.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-gray-500"
                            title="Send welcome email"
                            onClick={() => welcomeEmailMutation.mutate(a.email)}
                            disabled={welcomeEmailMutation.isPending && welcomeEmailMutation.variables === a.email}
                          >
                            <Mail size={13} /> Welcome
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-gray-500"
                            title="Send password reset email"
                            onClick={() => passwordResetMutation.mutate(a.email)}
                            disabled={passwordResetMutation.isPending && passwordResetMutation.variables === a.email}
                          >
                            <KeyRound size={13} /> Reset password
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
